import TeamLogo from '@/components/sport/TeamLogo'
import type { SportMatch } from '@/lib/matches-data'
import { teamLogoSrc } from '@/lib/standings-logos'
import { formatSiteDate, formatSiteDateTime } from '@/lib/site-datetime'

function isLive(status: string) {
  const s = status.toUpperCase()
  return s === 'IN_PLAY' || s === 'LIVE' || s === 'PAUSED'
}

function statusBadge(match: SportMatch): { text: string; className: string } {
  const s = match.status.toUpperCase()
  if (s === 'IN_PLAY' || s === 'LIVE') {
    return {
      text: match.statusLabel || 'Canlı',
      className: 'bg-red-50 text-red-600 dark:bg-red-950/40 dark:text-red-300',
    }
  }
  if (s === 'PAUSED') {
    return {
      text: match.statusLabel || 'Devre',
      className:
        'bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300',
    }
  }
  if (s === 'FINISHED') {
    return {
      text: 'Bitti',
      className:
        'bg-gray-100 text-gray-600 dark:bg-slate-800 dark:text-slate-300',
    }
  }
  return {
    text: match.statusLabel || 'Yakında',
    className: 'bg-sky-50 text-sky-700 dark:bg-sky-950/40 dark:text-sky-300',
  }
}

export default function MatchRow({
  match,
  showLeague = true,
  showDate = false,
  showMatchday = false,
}: {
  match: SportMatch
  showLeague?: boolean
  showDate?: boolean
  showMatchday?: boolean
}) {
  const live = isLive(match.status)
  const hasScore = match.score.home != null && match.score.away != null
  const sport = match.sport || 'soccer'
  const badge = statusBadge(match)

  const time = formatSiteDateTime(match.utcDate, {
    hour: '2-digit',
    minute: '2-digit',
  })
  const dateLabel = showDate
    ? formatSiteDate(match.utcDate, {
        day: 'numeric',
        month: 'short',
        weekday: 'short',
      })
    : null

  const homeName = match.home.name
  const awayName = match.away.name

  return (
    <div
      className={`border-b border-gray-50 px-3 py-3 last:border-b-0 dark:border-slate-800/80 sm:px-4 ${
        live ? 'bg-red-50/40 dark:bg-red-950/15' : ''
      }`}
    >
      <div className="sm:hidden">
        <div className="flex min-w-0 items-center gap-2">
          {dateLabel ? (
            <span className="truncate text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-slate-500">
              {dateLabel}
            </span>
          ) : null}
          <span className="text-xs font-black tabular-nums text-gray-800 dark:text-slate-100">
            {time}
          </span>
          <span
            className={`rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${badge.className}`}
          >
            {live ? (
              <span className="inline-flex items-center gap-1">
                <span className="size-1.5 animate-pulse rounded-full bg-red-600" />
                {badge.text}
              </span>
            ) : (
              badge.text
            )}
          </span>
          {showMatchday && match.matchday != null ? (
            <span className="ml-auto text-[10px] font-bold text-gray-400 dark:text-slate-500">
              Hafta {match.matchday}
            </span>
          ) : showLeague ? (
            <span className="ml-auto truncate text-[10px] text-gray-400 dark:text-slate-500">
              {match.leagueTitle}
            </span>
          ) : null}
        </div>

        <div className="mt-2 space-y-1.5">
          <MobileMatchTeam
            name={homeName}
            logo={match.home.crest || teamLogoSrc(sport, match.home.teamId)}
            score={match.score.home}
            live={live}
          />
          <MobileMatchTeam
            name={awayName}
            logo={match.away.crest || teamLogoSrc(sport, match.away.teamId)}
            score={match.score.away}
            live={live}
          />
        </div>
      </div>

      <div className="hidden grid-cols-[80px_1fr] items-center gap-2 sm:grid">
        <div className="flex min-w-0 flex-col items-start gap-1">
          {dateLabel ? (
            <div className="truncate text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-slate-500">
              {dateLabel}
            </div>
          ) : null}
          <div className="text-sm font-bold tabular-nums text-gray-800 dark:text-slate-100">
            {time}
          </div>
          <span
            className={`rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${badge.className}`}
          >
            {live ? (
              <span className="inline-flex items-center gap-1">
                <span className="size-1.5 animate-pulse rounded-full bg-red-600" />
                {badge.text}
              </span>
            ) : (
              badge.text
            )}
          </span>
          {showMatchday && match.matchday != null ? (
            <div className="text-[10px] font-bold text-gray-400 dark:text-slate-500">
              Hafta {match.matchday}
            </div>
          ) : null}
          {showLeague ? (
            <div className="truncate text-[10px] text-gray-400 dark:text-slate-500">
              {match.leagueTitle}
            </div>
          ) : null}
        </div>

        <div className="grid min-w-0 grid-cols-[1fr_auto_1fr] items-center gap-2">
          <div className="flex min-w-0 items-center justify-end gap-2">
            <div className="truncate text-right text-sm font-semibold text-gray-900 dark:text-white">
              {homeName}
            </div>
            <TeamLogo
              src={match.home.crest || teamLogoSrc(sport, match.home.teamId)}
              alt={homeName}
              size={24}
            />
          </div>

          <div className="flex min-w-[64px] flex-col items-center justify-center">
            {hasScore ? (
              <div
                className={`rounded-md px-2.5 py-1 text-sm font-black tabular-nums ${
                  live
                    ? 'bg-red-600 text-white'
                    : 'bg-gray-100 text-gray-900 dark:bg-slate-800 dark:text-white'
                }`}
              >
                {match.score.home} - {match.score.away}
              </div>
            ) : (
              <div className="rounded-md bg-gray-50 px-2.5 py-1 text-xs font-bold uppercase text-gray-400 dark:bg-slate-800/60 dark:text-slate-500">
                vs
              </div>
            )}
          </div>

          <div className="flex min-w-0 items-center justify-start gap-2">
            <TeamLogo
              src={match.away.crest || teamLogoSrc(sport, match.away.teamId)}
              alt={awayName}
              size={24}
            />
            <div className="truncate text-sm font-semibold text-gray-900 dark:text-white">
              {awayName}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function MobileMatchTeam({
  name,
  logo,
  score,
  live,
}: {
  name: string
  logo: string | null | undefined
  score: number | null
  live: boolean
}) {
  return (
    <div className="flex min-w-0 items-center gap-2">
      <TeamLogo src={logo} alt={name} size={24} />
      <span className="min-w-0 flex-1 truncate text-sm font-semibold text-gray-900 dark:text-white">
        {name}
      </span>
      <span
        className={`flex size-7 shrink-0 items-center justify-center rounded-md text-sm font-black tabular-nums ${
          live
            ? 'bg-red-600 text-white'
            : 'bg-gray-100 text-gray-900 dark:bg-slate-800 dark:text-white'
        }`}
      >
        {score ?? '—'}
      </span>
    </div>
  )
}
